import { cn, useFeatureFlag } from '@lensora/ui'
import {
  Building2,
  CreditCard,
  Globe,
  KeyRound,
  LineChart,
  Palette,
  Settings as SettingsIcon,
  Users,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { NavLink, Outlet } from 'react-router-dom'

import { isSelfServeClient, useMe } from '@/hooks/use-me'

function SettingsTab({
  to,
  end,
  icon: Icon,
  children,
}: {
  to: string
  end?: boolean
  icon: LucideIcon
  children: React.ReactNode
}) {
  return (
    <NavLink
      to={to}
      end={end ?? false}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 rounded px-3 py-2 text-sm transition-colors',
          isActive
            ? 'bg-brand-primary font-medium text-brand-primary-foreground'
            : 'text-text-muted hover:bg-surface-raised hover:text-text'
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {children}
    </NavLink>
  )
}

export function SettingsLayout() {
  const { data } = useMe()
  const billingEnabled = useFeatureFlag('billing_enabled')
  const role = data?.user.role
  const isClient = role === 'client'
  const isOwnerOrAdmin = role === 'owner' || role === 'admin'
  const isSelfServe = isSelfServeClient(data)

  return (
    <div className="flex h-full gap-6">
      <nav className="w-48 shrink-0 space-y-0.5">
        <SettingsTab to="/dashboard/settings" end icon={SettingsIcon}>
          General
        </SettingsTab>

        {!isClient && (
          <SettingsTab to="/dashboard/settings/team" icon={Users}>
            Team
          </SettingsTab>
        )}

        {isOwnerOrAdmin && (
          <SettingsTab to="/dashboard/settings/clients" icon={Building2}>
            Clients
          </SettingsTab>
        )}

        <SettingsTab to="/dashboard/settings/websites" icon={Globe}>
          Websites
        </SettingsTab>

        {isOwnerOrAdmin && (
          <SettingsTab to="/dashboard/settings/mcp" icon={KeyRound}>
            Lenora MCP
          </SettingsTab>
        )}

        {(isOwnerOrAdmin || isSelfServe) && (
          <SettingsTab to="/dashboard/settings/looker" icon={LineChart}>
            Looker Studio
          </SettingsTab>
        )}

        {!isClient && billingEnabled && (
          <SettingsTab to="/dashboard/settings/brand" icon={Palette}>
            Brand
          </SettingsTab>
        )}

        {/* Self-serve clients' billing visibility is authoritative from
            their own subscription state, not the tenant-admin flag. */}
        {((!isClient && billingEnabled) || isSelfServe) && (
          <SettingsTab to="/dashboard/settings/billing" icon={CreditCard}>
            Billing
          </SettingsTab>
        )}
      </nav>

      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  )
}
